import { lang } from '@umu/ukit-lang';
import './index.scss';
import { Iconfont } from '@umu/umu-design';
import React, { useCallback } from 'react';

interface IProps {
    className?: string;
    currentPage: number;
    maxPage: number;
    forceShow?: boolean;
    pageOffset?: number;
    showSizeChanger?: boolean; // 小方格的风格
    onPageChange?(page: number): void;
}

const PAGE_OFFSET = 4;

export const PaginationV2 = ({
    currentPage,
    maxPage,
    forceShow,
    className = '',
    showSizeChanger,
    pageOffset = PAGE_OFFSET,
    ...rest
}: IProps) => {
    currentPage = Number(currentPage);
    maxPage = Number(maxPage);
    let start = currentPage - pageOffset;
    let end = currentPage + pageOffset;

    // 确保最多展示 2*offset+1 个
    if (start < 1) {
        const padding = Math.abs(1 - start);
        start += padding;
        end += padding;
    } else if (end > maxPage) {
        const padding = Math.abs(end - maxPage);
        start -= padding;
        end -= padding;
    }

    // 防止越界
    start = Math.max(1, start);
    end = Math.min(end, maxPage);

    // 生成页码列表
    const list = Array.from(Array(end - start + 1).keys()).map((_) => _ + start);

    const showHead = currentPage === 1;
    const showTail = currentPage === maxPage;
    const showJumpPrev = list.indexOf(1) === -1;
    const showJumpNext = list.indexOf(maxPage) === -1;
    const showJumpPerPage = maxPage > 5;

    const onClick = useCallback(
        (page: number) => {
            if (page === currentPage) {
                return;
            }
            if (rest.onPageChange) {
                rest.onPageChange(page);
            }
        },
        [currentPage, rest.onPageChange],
    );

    const onJump = useCallback(
        (offset: number) => {
            let page = currentPage + offset;
            if (page < 1) {
                page = 1;
            } else if (page > maxPage) {
                page = maxPage;
            }
            onClick(page);
        },
        [currentPage, rest.onPageChange],
    );

    if (!forceShow && list.length <= 1) {
        return null;
    }

    return (
        <div className={`umu-pagination-bar ${showSizeChanger ? 'umu-pagination-bar-box' : 'umu-pagination-bar-normal'} ${className}`}>
            <ul className="page-list">
                {!showHead ? (
                    <li className="page-item" onClick={() => onClick(1)}>
                        {lang('首页_First')}
                    </li>
                ) : null}
                {showJumpPerPage && !showHead ? (
                    <li className="page-item page-item-prev" onClick={() => onJump(-1)}>
                        <Iconfont
                            type="SmallBack"
                            className={showSizeChanger ? 'small-more-icon gray' : 'small-more-icon blue'}
                        />
                    </li>
                ) : null}
                {showJumpPrev ? (
                    <li className="page-item" onClick={() => onJump(-10)}>
                        ...
                    </li>
                ) : null}
                {list.map((page) => {
                    return (
                        <li
                            key={page}
                            className={'page-item' + (page === currentPage ? ' current' : '')}
                            onClick={() => onClick(page)}
                        >
                            {page}
                        </li>
                    );
                })}
                {showJumpNext ? (
                    <li className="page-item" onClick={() => onJump(10)}>
                        ...
                    </li>
                ) : null}
                {forceShow || (showJumpPerPage && !showTail) ? (
                    <li className="page-item page-item-next" onClick={() => onJump(1)}>
                        <Iconfont
                            type="SmallMore"
                            className={showSizeChanger ? 'small-more-icon gray' : 'small-more-icon blue'}
                        />
                    </li>
                ) : null}
                {!showTail || forceShow ? (
                    <li className="page-item" onClick={() => onClick(maxPage)}>
                        {lang('尾页_Last')}
                    </li>
                ) : null}
            </ul>
        </div>
    );
};
